import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatusBadge } from "@/components/ui/status-badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { humanize, RISK_ITEM_LABELS, TIER_LABEL } from "../../lib/careplan"
import type { TcmRisk, TcmRiskScore, TcmRiskTier } from "../../types"

export function riskVariant(tier: TcmRiskTier): "destructive" | "warning" | "muted" {
  return tier === "HIGH" ? "destructive" : tier === "INTERMEDIATE" ? "warning" : "muted"
}

/** "High readmission risk", or "≥ Medium" when a missing record could push it higher. */
export function RiskChip({ tier, complete }: { tier: TcmRiskTier; complete: boolean }) {
  if (tier === "NA") return <StatusBadge variant="muted">Risk not scored</StatusBadge>
  return (
    <StatusBadge variant={riskVariant(tier)} dot>
      {complete || tier === "HIGH" ? "" : "≥ "}
      {TIER_LABEL[tier]} readmission risk
    </StatusBadge>
  )
}

const ABOUT: Record<string, string> = {
  HOSPITAL: "Potentially avoidable readmission within 30 days (Donzé 2013; validated in 117,065 medical discharges).",
  LACE: "Death or unplanned readmission within 30 days (van Walraven 2010).",
}

function points(i: { points_min: number; points_max: number }) {
  return i.points_min === i.points_max ? String(i.points_min) : `${i.points_min}–${i.points_max}`
}

function itemNote(status: string): string {
  if (status === "unknown") return "Not in the records"
  if (status === "at_least") return "At least (more may be missing)"
  if (status === "not_applicable") return "Not applicable"
  return ""
}

function ScoreTable({ score }: { score: TcmRiskScore }) {
  const range = score.min === score.max ? String(score.min) : `${score.min}–${score.max}`
  const name = (t: string) => TIER_LABEL[t as TcmRiskTier] ?? humanize(t.toLowerCase())
  const tier = score.tier_min === score.tier_max ? name(score.tier_min) : `${name(score.tier_min)} to ${name(score.tier_max).toLowerCase()}`
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-baseline justify-between gap-2 text-sm">
          <span>{score.name} score</span>
          <span className="text-xs font-normal text-muted-foreground">
            {range} points · {tier}
          </span>
        </CardTitle>
        <p className="text-xs text-muted-foreground">{ABOUT[score.name] ?? ""}</p>
      </CardHeader>
      <CardContent className="px-0 pb-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="h-8 text-xs">Item</TableHead>
              <TableHead className="h-8 w-20 text-right text-xs">Points</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {score.items.map((i) => (
              <TableRow key={i.key}>
                <TableCell className="py-2 text-[13px]">
                  {RISK_ITEM_LABELS[i.key] ?? humanize(i.key)}
                  {itemNote(i.status) && <span className="block text-[11px] text-muted-foreground">{itemNote(i.status)}</span>}
                </TableCell>
                <TableCell className="py-2 text-right text-[13px] tabular-nums">{points(i)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

/** Both validated scores with every item, what was missing, and the named drivers behind them. */
export function RiskPanel({ risk }: { risk: TcmRisk }) {
  if (!risk.scored) {
    return (
      <p className="text-sm text-muted-foreground">
        Not scored: both scores were validated on hospital stays, not ED visits.
      </p>
    )
  }
  const drivers = [
    ...risk.drivers.map((d) => RISK_ITEM_LABELS[d.key] ?? humanize(d.key)),
    ...risk.clinical_drivers.map((d) => humanize(d.key)),
  ]
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <RiskChip tier={risk.tier} complete={risk.tier_complete} />
        {!risk.tier_complete && risk.tier !== "HIGH" && (
          <span className="text-xs text-muted-foreground">Some inputs weren't in the records, so the risk could be higher.</span>
        )}
      </div>
      {drivers.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">What drives it</p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {Array.from(new Set(drivers)).map((d) => (
              <li key={d}>
                <StatusBadge variant="muted" size="sm">{d}</StatusBadge>
              </li>
            ))}
          </ul>
        </div>
      )}
      {risk.warnings.map((w) => (
        <p key={w} className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">{w}</p>
      ))}
      <div className="grid gap-4 lg:grid-cols-2">
        {risk.hospital && <ScoreTable score={risk.hospital} />}
        {risk.lace && <ScoreTable score={risk.lace} />}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Scores are computed in code from facts cited in the hospital records, never blended or adjusted. Both were
        validated on medical discharges; read surgical and rehab stays with care.
      </p>
    </div>
  )
}
